"use client";

import { useEffect, useRef } from "react";

/**
 * Re-ask the server whether a pending payment has settled.
 *
 * ## Why this has to exist
 *
 * The pending copy promises "if you approved it in your UPI app it will confirm
 * shortly". That was true of the database — the Razorpay webhook settles the
 * booking either way — and false of the screen, which checked once on mount and
 * then never again. Somebody who approved the collect on their phone sat looking
 * at a payment page that had already been told the payment failed, while their
 * ticket existed. The promise is now kept by actually re-checking.
 *
 * ## Why polling rather than a socket
 *
 * The settling event arrives at the *server* from Razorpay, on a webhook, up to
 * a couple of minutes after the payer approves. There is nothing to subscribe to
 * from here, and the check is a cheap indexed read that also self-heals by
 * querying Razorpay directly when the webhook is late.
 */
export function usePaymentStatusPoll({
  enabled,
  check,
  intervalMs = 5000,
  maxDurationMs = 5 * 60 * 1000,
}: {
  enabled: boolean;
  /** Resolve `true` once the payment reached a terminal state and polling can stop. */
  check: () => Promise<boolean>;
  intervalMs?: number;
  /**
   * Matched to the NPCI approval window. Past this the payer has almost
   * certainly abandoned it, and the webhook remains the backstop regardless.
   */
  maxDurationMs?: number;
}) {
  // Kept in a ref so a caller can pass an inline closure without restarting the
  // poll on every render — the alternative is a `useCallback` obligation at every
  // call site that is easy to get wrong and silently resets the deadline.
  const checkRef = useRef(check);
  checkRef.current = check;

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    let stopped = false;
    let timer: number | undefined;
    const deadline = Date.now() + maxDurationMs;
    let inFlight = false;

    const stop = () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };

    const schedule = () => {
      if (stopped) return;
      if (Date.now() >= deadline) return stop();
      timer = window.setTimeout(run, intervalMs);
    };

    const run = async () => {
      if (stopped) return;
      // A backgrounded tab is a person in their bank app. Skip the round trip
      // and keep the schedule alive so the answer is ready when they return.
      if (document.visibilityState !== "visible") return schedule();
      if (inFlight) return schedule();

      inFlight = true;
      try {
        const settled = await checkRef.current();
        if (settled) return stop();
      } catch {
        // Transient failures are expected here (the payment is mid-flight and
        // the network may be too). Keep polling; the deadline bounds it.
      } finally {
        inFlight = false;
      }
      schedule();
    };

    // Coming back from the bank app is the single most likely moment for the
    // status to have changed, so check immediately rather than waiting out the
    // interval the backgrounded tab just spent skipping.
    const onVisibility = () => {
      if (document.visibilityState !== "visible" || stopped) return;
      if (timer !== undefined) window.clearTimeout(timer);
      void run();
    };
    document.addEventListener("visibilitychange", onVisibility);

    schedule();

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, intervalMs, maxDurationMs]);
}
